import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { BadgeCopyComponent } from './rsabt-indicator-copy.component';
import { IndicatorState } from '../../../models/indicator.models';
import { IndicatorStates } from '../../../constants/indicators.constants';
import { TooltipDirective } from '../../atoms/tooltip/tooltip.directive';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { truncate } from '@ui';
import { TenantsService } from '../../../../app/core/services/tenants.service';

@Component({
  selector: 'ui-administration-center-indicator',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BadgeCopyComponent, TooltipDirective, NzTagModule],
  template: `
    <div class="relative">
      <div class="flex flex-col gap-y-1">
        <div class="flex gap-x-2">
          @if(showCenterCode()){
            <nz-tag nzColor="#D6E4FF" class="text-black m-0 rounded-[3px] h-[fit-content]">{{ distributorId() }}</nz-tag>
          }
          <span
            uiTooltip
            [tooltipTitle]="nameTemplate"
            class="text-sm font-medium uppercase duration-300 transition-colors"
            [class]="stateClassMap[state()].label"
            (click)="emitCenter()"
          >
            {{ truncatedName() }}
          </span>
        </div>
        <ui-badge-copy [id]="centerId()!" [disabled]="isDisabled()" />
      </div>
    </div>

    <ng-template #nameTemplate>
      {{ name() }}
    </ng-template>
  `,
})
export class AdministrationCenterIndicatorComponent {
  protected readonly TRUNCATE_MAX_CHARS: number = 72;
  private readonly tenantsService = inject(TenantsService);

  state = input<IndicatorState>(IndicatorStates.Default);
  showCenterCode = computed(
    () => this.tenantsService.selectedTenants().length > 1,
  );
  name = input.required<string>();
  distributorId = input.required<string>();
  centerId = input.required<string>();
  rsabtId = input<string>();
  tsabtId = input<string>();
  vtnId = input<string>();

  onCenter = output<{
    distributorId: string;
    centerId: string;
    rsabtId: string;
    tsabtId: string;
    vtnId: string;
  }>();

  isDisabled = computed(() => this.state() === IndicatorStates.Disabled);
  truncatedName = computed(() => {
    const maxChars = this.showCenterCode()
      ? this.TRUNCATE_MAX_CHARS
      : this.TRUNCATE_MAX_CHARS + 5;
    return truncate(this.name()!, maxChars);
  });

  emitCenter() {
    this.onCenter.emit({
      distributorId: this.distributorId()!,
      centerId: this.centerId()!,
      rsabtId: this.rsabtId()!,
      tsabtId: this.tsabtId()!,
      vtnId: this.vtnId()!,
    });
  }

  stateClassMap: Record<IndicatorState, { code: string; label: string }> = {
    active: {
      code: 'text-black bg-[#D6E4FF]',
      label: 'cursor-pointer text-primary hover:text-tertiary',
    },
    disabled: { code: 'bg-neutral-1 text-neutral-6', label: 'text-neutral-6' },
    danger: {
      code: 'text-black bg-red-2',
      label: 'cursor-pointer text-error hover:text-black',
    },
  };
}
